import type { ReactNode } from 'react';

import { Card, CardTitle, cn, type StatusTone } from '@giyus/ui';

export interface ModuleSummaryRow {
  readonly id: string;
  readonly label: ReactNode;
  readonly value: ReactNode;
  readonly tone?: StatusTone;
  /** Texto do tom para leitores de tela. */
  readonly toneLabel?: string;
}

export type ModuleSummaryState = 'attention' | 'ok' | 'unavailable';

export interface ModuleSummaryCardProps {
  /** Nome do módulo (ex.: "Pessoas"). */
  readonly title: ReactNode;
  /** `attention`: há pendências · `ok`: dados reais sem pendências · `unavailable`: o módulo ainda não expõe esse indicador. */
  readonly state: ModuleSummaryState;
  /** Texto da situação (ex.: "2 alertas", "Sem pendências", "Indicador ainda indisponível"). */
  readonly stateLabel: string;
  readonly rows?: readonly ModuleSummaryRow[];
  /** Explicação quando `unavailable` (honesto: diz o que falta; nunca números). */
  readonly unavailableText?: ReactNode;
  /** Link para a tela do módulo. */
  readonly action?: ReactNode;
  readonly headingLevel?: 'h3' | 'h4';
  readonly className?: string;
}

const STATE_CLASS = {
  attention: 'bg-warning',
  ok: 'bg-success',
  unavailable: 'bg-muted-foreground',
};

/** Resumo de alertas de um módulo, com três estados honestos: pendências, sem pendências e indicador indisponível. */
export function ModuleSummaryCard({
  title,
  state,
  stateLabel,
  rows = [],
  unavailableText,
  action,
  headingLevel = 'h3',
  className,
}: ModuleSummaryCardProps) {
  return (
    <Card
      data-slot="module-summary-card"
      data-state={state}
      className={cn('min-w-0 gap-3 p-4', className)}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle as={headingLevel} className="text-body font-semibold">
          {title}
        </CardTitle>
        <span className="inline-flex size-3 items-center justify-center">
          <span className={cn('size-1.5 rounded-pill', STATE_CLASS[state])} aria-hidden="true" />
          <span className="sr-only">{stateLabel}</span>
        </span>
      </div>
      {state === 'unavailable' ? (
        unavailableText ? (
          <p className="text-body-sm text-muted-foreground">{unavailableText}</p>
        ) : null
      ) : rows.length > 0 ? (
        <ul className="flex flex-col gap-1.5">
          {rows.map((row) => (
            <li key={row.id} className="flex items-baseline justify-between gap-3 text-body-sm">
              <span className="text-foreground-secondary">{row.label}</span>
              <span
                className={cn(
                  'shrink-0 font-data font-medium',
                  row.tone === 'danger'
                    ? 'text-danger-subtle-foreground'
                    : row.tone === 'warning'
                      ? 'text-warning-subtle-foreground'
                      : 'text-heading',
                )}
              >
                {row.toneLabel ? <span className="sr-only">{row.toneLabel}: </span> : null}
                {row.value}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {action ? <div className="mt-auto text-body-sm">{action}</div> : null}
    </Card>
  );
}
